// Éléments communs du portail citoyen : en-tête de page avec retour, sections à filets et lignes
// cliquables (écran S20), statut en texte coloré, bloc mis en avant, garde de connexion, état
// honnête d'une rubrique livrée par un lot ultérieur.
import { EtatVide, LienBouton, Squelette, classes, classesBouton } from '@cc/ui';
import { ChevronLeft, ChevronRight, Construction, UserRound } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useSessionUsager } from './session/FournisseurSessionUsager';
import { libelleStatut, tonStatut, type TonStatut } from './statuts';
import styles from './citoyen.module.css';

export interface ProprietesEnTeteCitoyen {
  titre: string;
  surTitre?: ReactNode;
  sousTitre?: ReactNode;
  /** Adresse du retour (bouton de 44 px à gauche du titre). */
  retour?: string;
  libelleRetour?: string;
  actions?: ReactNode;
}

/** En-tête de page : retour, titre (h1 focalisé à l'arrivée), sous-titre, actions. */
export function EnTeteCitoyen({ titre, surTitre, sousTitre, retour, libelleRetour = 'Retour', actions }: ProprietesEnTeteCitoyen) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${titre} - Portail citoyen - CollectivitésConnect`;
  }, [titre]);
  return (
    <header className={styles.entetePage}>
      {retour ? (
        <div className={styles.ligneRetour}>
          <Link to={retour} className={styles.retour} aria-label={libelleRetour}>
            <ChevronLeft aria-hidden strokeWidth={2} width={20} height={20} />
          </Link>
          <span className={styles.aide}>{libelleRetour}</span>
        </div>
      ) : null}
      {surTitre ? <p className={styles.surTitre}>{surTitre}</p> : null}
      <h1 ref={ref} tabIndex={-1} className={styles.titrePage}>
        {titre}
      </h1>
      {sousTitre ? <p className={styles.sousTitre}>{sousTitre}</p> : null}
      {actions ? <div className={styles.actionsPage}>{actions}</div> : null}
    </header>
  );
}

export function Section({ titre, lien, children, id }: { titre: string; lien?: { href: string; libelle: string }; children: ReactNode; id?: string }) {
  const idTitre = `${id ?? titre.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-titre`;
  return (
    <section className={styles.section} aria-labelledby={idTitre}>
      <div className={styles.enteteSection}>
        <h2 id={idTitre} className={styles.titreSection}>
          {titre}
        </h2>
        {lien ? (
          <Link to={lien.href} className={styles.lienSection}>
            {lien.libelle}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

export interface ProprietesLigne {
  href?: string;
  onClick?: () => void;
  titre: ReactNode;
  sousTitre?: ReactNode;
  droite?: ReactNode;
  ariaLabel?: string;
}

/** Ligne à filet : titre, sous-titre, mention à droite, chevron si elle mène quelque part. */
export function Ligne({ href, onClick, titre, sousTitre, droite, ariaLabel }: ProprietesLigne) {
  const contenu = (
    <>
      <span className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>{titre}</span>
        {sousTitre ? <span className={styles.ligneSous}>{sousTitre}</span> : null}
      </span>
      {droite !== undefined ? <span className={styles.ligneDroite}>{droite}</span> : <span />}
      {href || onClick ? <ChevronRight className={styles.chevron} aria-hidden strokeWidth={2} width={14} height={14} /> : null}
    </>
  );
  if (href) {
    return (
      <li>
        <Link to={href} className={styles.ligne} aria-label={ariaLabel}>
          {contenu}
        </Link>
      </li>
    );
  }
  if (onClick) {
    return (
      <li>
        <button type="button" className={styles.ligne} onClick={onClick} aria-label={ariaLabel}>
          {contenu}
        </button>
      </li>
    );
  }
  return <li className={classes(styles.ligne, styles.ligneSansChevron)}>{contenu}</li>;
}

export function Liste({ children, libelle }: { children: ReactNode; libelle?: string }) {
  return (
    <ul className={styles.liste} aria-label={libelle}>
      {children}
    </ul>
  );
}

const CLASSE_TON: Record<TonStatut, string | undefined> = {
  neutre: styles.statutNeutre,
  info: styles.statutInfo,
  attention: styles.statutAttention,
  ok: styles.statutOk,
};

/** Statut en texte coloré ; le libellé porte toujours l'information. */
export function TexteStatut({ code, libelle, ton }: { code?: string; libelle?: string; ton?: TonStatut }) {
  const t = ton ?? (code ? tonStatut(code) : 'neutre');
  return <span className={classes(styles.statut, CLASSE_TON[t])}>{libelle ?? (code ? libelleStatut(code) : '')}</span>;
}

export function Bloc({ etiquette, titre, children, className }: { etiquette?: string; titre?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={classes(styles.bloc, className)} aria-label={typeof titre === 'string' ? titre : etiquette}>
      {etiquette ? <span className={styles.blocEtiquette}>{etiquette}</span> : null}
      {titre ? <h2 className={styles.blocTitre}>{titre}</h2> : null}
      {children}
    </section>
  );
}

/** Rubrique prévue par un lot ultérieur : état honnête, aucun faux formulaire. */
export function RubriqueAVenir({ titre, lot, description }: { titre: string; lot: string; description: string }) {
  return (
    <>
      <EnTeteCitoyen titre={titre} />
      <EtatVide titre={`Disponible au lot ${lot}`} icone={Construction} niveau={2}>
        <p>{description}</p>
      </EtatVide>
    </>
  );
}

/**
 * Exige une session usager : squelette pendant l'ouverture, invitation à se connecter sinon.
 * L'adresse demandée est mémorisée pour y revenir après la connexion.
 */
export function ExigeConnexion({ children, titre = 'Connectez-vous pour continuer' }: { children: ReactNode; titre?: string }) {
  const { etat, chargementProfil } = useSessionUsager();
  const location = useLocation();
  if (etat === 'initialisation' || chargementProfil) return <Squelette lignes={4} libelle="Ouverture de la session" />;
  if (etat === 'compte_agent') {
    return (
      <EtatVide titre="Compte agent connecté" icone={UserRound} niveau={2}>
        <p>Le portail citoyen est réservé aux comptes usagers. Votre compte d&apos;agent reste connecté à l&apos;espace Administration.</p>
        <p className={styles.aide}>Déconnectez-vous de l&apos;espace Administration, puis créez ou utilisez un compte usager.</p>
      </EtatVide>
    );
  }
  if (etat === 'deconnecte') {
    const depuis = `${location.pathname}${location.search}`;
    return (
      <EtatVide
        titre={titre}
        icone={UserRound}
        niveau={2}
        action={
          <div className={styles.ligneBoutons}>
            <Link to="/citoyen/connexion" state={{ depuis }} className={classesBouton('principal')}>
              Se connecter
            </Link>
            <LienBouton href="/citoyen/inscription" variante="secondaire">
              Créer un compte
            </LienBouton>
          </div>
        }
      >
        <p>Vos demandes, pièces, rendez-vous et réclamations sont rattachés à votre compte usager, le même pour toutes les collectivités.</p>
      </EtatVide>
    );
  }
  return <>{children}</>;
}
